import React from 'react'
import { Link } from 'react-router-dom'
import type { StoreV2, Lang } from './types'

interface Props {
  store: StoreV2
  lang: Lang
  isComingSoon?: boolean
}

const FALLBACK_IMG =
  'https://images.pexels.com/photos/1307698/pexels-photo-1307698.jpeg?auto=compress&cs=tinysrgb&w=1600'

const formatHours = (s: StoreV2, lang: Lang): string => {
  if (s.hours) return s.hours
  if (s.businessHours?.monday) {
    return lang === 'zh'
      ? `週一至週日 ${s.businessHours.monday}`
      : `Daily ${s.businessHours.monday}`
  }
  return lang === 'zh' ? '依各分店公告' : 'See store for hours'
}

const formatAddress = (addr: any): string => {
  if (!addr) return ''
  if (typeof addr === 'string') return addr
  return addr.full || [addr.district, addr.street].filter(Boolean).join(' ')
}

const StoreCardV2: React.FC<Props> = ({ store, lang, isComingSoon }) => {
  const displayName = lang === 'en' ? store.nameEn || store.name : store.name
  const cover = store.image || store.images?.[0] || FALLBACK_IMG
  const address = formatAddress(store.address)
  // V2 store cards link to V2 detail pages when available (currently: xinyi)
  const hasV2Detail = store.slug === 'xinyi'
  const detailHref = hasV2Detail
    ? `/${lang}/store/${store.slug}/v2`
    : `/${lang}/store/${store.slug}`

  const t = {
    visit: lang === 'zh' ? '前往門市' : 'Visit store',
    map: lang === 'zh' ? 'Google 地圖' : 'Google Maps',
    soon: lang === 'zh' ? '即將開放' : 'Coming soon',
    hours: lang === 'zh' ? '營業時間' : 'Hours',
  }

  return (
    <article className='group'>
      {/* Square cover image, click goes to detail page */}
      <Link to={detailHref} className='block overflow-hidden bg-black/[0.04]'>
        <div className='aspect-square w-full overflow-hidden'>
          <img
            src={cover}
            alt={displayName}
            loading='lazy'
            className='h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]'
          />
        </div>
      </Link>

      {/* Minimal info block — Barista Map style */}
      <div className='pt-6'>
        <h3
          className='text-[22px] md:text-[26px] leading-[1.25] text-black mb-3'
          style={{ fontFamily: "'EB Garamond', 'Noto Serif TC', serif", fontWeight: 400 }}
        >
          <Link to={detailHref} className='hover:text-black/60 transition-colors'>
            {displayName}
          </Link>
        </h3>

        {address ? (
          <p className='text-[13px] leading-relaxed text-black/70 font-light tracking-wide mb-1.5'>
            {address}
          </p>
        ) : null}

        <p className='text-[13px] leading-relaxed text-black/60 font-light tracking-wide'>
          {t.hours}｜{formatHours(store, lang)}
        </p>

        {store.phone ? (
          <p className='text-[13px] leading-relaxed text-black/60 font-light tracking-wide'>
            {store.phone}
          </p>
        ) : null}

        {/* Action row: detail + map */}
        <div className='mt-5 flex items-center gap-6 text-[12px] tracking-[0.18em] uppercase'>
          {isComingSoon ? (
            <span className='text-black/40'>{t.soon}</span>
          ) : (
            <Link
              to={detailHref}
              className='border-b border-black pb-0.5 text-black hover:text-black/60 hover:border-black/40 transition-colors'
            >
              {t.visit}
            </Link>
          )}

          {store.googleMapsUrl ? (
            <a
              href={store.googleMapsUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='text-black/60 hover:text-black transition-colors'
            >
              {t.map} ↗
            </a>
          ) : null}
        </div>
      </div>
    </article>
  )
}

export default StoreCardV2
